// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/components/ui/select.tsx (base-ui compound Select: `Select` root with
// `value` / `onValueChange` / `disabled`, `SelectTrigger` (`className`, `size` "sm" | "default", `aria-label`),
// `SelectValue` (`placeholder`, optional label children), `SelectContent` (`className`), `SelectItem` (`value`, label
// children), `SelectGroup`).
// Wraps:   Signal DS `Select` (prop-driven: `options` / `value` / `onChange` / `placeholder` / `size`).
// Overrides: the compound tree is LIFTED into DS props at render: the root walks its children for the trigger (its
// `aria-label`, `size`, `className`), the value (its `placeholder`) and every `SelectItem` (an option: value plus its
// text as the label, the node itself as `renderLabel` when it is not plain text). The DS resolves the shown label from
// the options, which is what PM's call sites render by hand through `SelectValue` children (base-ui shows the raw
// value otherwise), so those children are not needed and are ignored. Two Base UI traps are absorbed here, not at the
// call site: the value is kept CONTROLLED with `''` (Base UI fixes controlled-vs-uncontrolled at mount, so `undefined`
// would freeze the field), and a `null` the DS Select emits when its option list changes is forwarded as `null`, which
// is what PM's handlers already expect (`v ?? ''`, `v && …`). `SelectContent` className (PM's popup width/height) has no
// DS seam: the DS popup is portalled and sized by the DS, so it is dropped (PROD-3310 gap).
import {
  Children,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Select, type SelectOption } from '@scripsteam/scrips-signal-ds';

export interface BridgeSelectProps {
  value?: string | null;
  onValueChange?: (value: string | null) => void;
  disabled?: boolean;
  name?: string;
  required?: boolean;
  children?: ReactNode;
}

export interface SelectTriggerProps {
  className?: string;
  size?: 'sm' | 'default';
  id?: string;
  'aria-label'?: string;
  children?: ReactNode;
}

export interface SelectValueProps {
  placeholder?: string;
  /** Ignored: the DS Select renders the chosen option's label itself. */
  children?: ReactNode | ((value: unknown) => ReactNode);
  className?: string;
}

export interface SelectContentProps {
  /** No DS seam (the popup is the DS's own); kept for PM call-site parity. */
  className?: string;
  children?: ReactNode;
}

export interface SelectItemProps {
  value: string;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
}

/** Marker parts: they render nothing themselves; the root reads their props. */
export const SelectTrigger: (props: SelectTriggerProps) => null = () => null;
export const SelectValue: (props: SelectValueProps) => null = () => null;
export const SelectContent: (props: SelectContentProps) => null = () => null;
export const SelectItem: (props: SelectItemProps) => null = () => null;
export function SelectGroup({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}

/** Plain text of a node tree ("Included", or "CPT 99213 · 120.00" from nested spans). */
export function textOf(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

interface Lifted {
  trigger?: SelectTriggerProps;
  placeholder?: string;
  options: SelectOption[];
}

function lift(children: ReactNode, into: Lifted): Lifted {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const el = child as ReactElement<unknown>;
    if (el.type === SelectTrigger) {
      const p = el.props as SelectTriggerProps;
      into.trigger = p;
      lift(p.children, into);
    } else if (el.type === SelectValue) {
      into.placeholder = (el.props as SelectValueProps).placeholder;
    } else if (el.type === SelectItem) {
      const p = el.props as SelectItemProps;
      const label = textOf(p.children);
      const plain = typeof p.children === 'string' || typeof p.children === 'number';
      into.options.push({
        value: p.value,
        label,
        disabled: p.disabled,
        ...(plain || p.children == null ? {} : { renderLabel: p.children }),
      });
    } else if (el.type === SelectContent || el.type === SelectGroup || el.type === Fragment) {
      lift((el.props as { children?: ReactNode }).children, into);
    }
  });
  return into;
}

export function BridgeSelect({ value, onValueChange, disabled, name, required, children }: BridgeSelectProps) {
  const { trigger, placeholder, options } = lift(children, { options: [] });
  return (
    <Select<string>
      options={options}
      value={value ?? ''}
      onChange={(v) => onValueChange?.(v ?? null)}
      placeholder={placeholder ?? ''}
      disabled={disabled}
      name={name}
      required={required}
      id={trigger?.id}
      aria-label={trigger?.['aria-label']}
      size={trigger?.size === 'sm' ? 'compact' : 'default'}
      className={trigger?.className}
    />
  );
}
